import { MatchResult } from './insightsClubSummary';
import { MatchBundle } from './insightsQueries';
import { TwPlayerBoardRow, TwPlayerMatchLine } from './insightsTwPlayerTables';

export interface ResultSplitRow {
    result: MatchResult | 'all';
    label: string;
    matches: number;
    goals: number;
    shots: number;
    assists: number;
    exclusionsDrawn: number;
    turnovers: number;
    saves: number;
    sog: number;
}

const emptySplit = (result: MatchResult | 'all', label: string): ResultSplitRow => ({
    result,
    label,
    matches: 0,
    goals: 0,
    shots: 0,
    assists: 0,
    exclusionsDrawn: 0,
    turnovers: 0,
    saves: 0,
    sog: 0,
});

export const matchResultForUs = (
    bundle: MatchBundle,
    ourTeamIds: number[]
): MatchResult | undefined => {
    const { detail } = bundle;
    const isHome = ourTeamIds.includes(detail.homeTeamId);
    const isAway = ourTeamIds.includes(detail.awayTeamId);
    if (!isHome && !isAway) return undefined;
    const gf = isHome ? detail.homeTeamGoalsTotal : detail.awayTeamGoalsTotal;
    const ga = isHome ? detail.awayTeamGoalsTotal : detail.homeTeamGoalsTotal;
    if (gf == null || ga == null) return undefined;
    if (gf > ga) return 'W';
    if (gf < ga) return 'L';
    return 'D';
};

export const buildWinLossSplits = (lines: TwPlayerMatchLine[]): ResultSplitRow[] => {
    const all = emptySplit('all', 'All');
    const wins = emptySplit('W', 'Wins');
    const draws = emptySplit('D', 'Draws');
    const losses = emptySplit('L', 'Losses');

    const apply = (row: ResultSplitRow, line: TwPlayerMatchLine): void => {
        row.matches += 1;
        row.goals += line.board.shots.total.goals;
        row.shots += line.board.shots.total.attempts;
        row.assists += line.board.assists;
        row.exclusionsDrawn += line.board.exclusionsDrawn;
        row.turnovers += line.board.turnovers;
        row.saves += line.board.saves.total.saves;
        row.sog += line.board.saves.total.attempts;
    };

    lines.forEach((line) => {
        apply(all, line);
        if (line.result === 'W') apply(wins, line);
        else if (line.result === 'D') apply(draws, line);
        else if (line.result === 'L') apply(losses, line);
    });

    return [all, wins, draws, losses].filter((row) => row.matches > 0 || row.result === 'all');
};

export interface CompareMetric {
    key: string;
    label: string;
    pick: (row: TwPlayerBoardRow) => number | string;
}

export const COMPARE_METRICS: CompareMetric[] = [
    { key: 'matches', label: 'Matches', pick: (r) => r.matches },
    { key: 'goals', label: 'Goals', pick: (r) => r.shots.total.goals },
    { key: 'shots', label: 'Shots', pick: (r) => r.shots.total.attempts },
    {
        key: 'shooting',
        label: 'Shooting %',
        pick: (r) =>
            r.shots.total.attempts > 0
                ? Math.round((r.shots.total.goals / r.shots.total.attempts) * 100)
                : '-',
    },
    { key: 'assists', label: 'Assists', pick: (r) => r.assists },
    { key: 'exD', label: 'Ex drawn', pick: (r) => r.exclusionsDrawn },
    { key: 'exC', label: 'Ex committed', pick: (r) => r.exclusionsCommitted },
    { key: 'turnovers', label: 'Turnovers', pick: (r) => r.turnovers },
    { key: 'blocks', label: 'Blocks', pick: (r) => r.blocks },
    { key: 'steals', label: 'Steals', pick: (r) => r.steals },
    { key: 'saves', label: 'Saves', pick: (r) => r.saves.total.saves },
    {
        key: 'savePct',
        label: 'Save %',
        pick: (r) =>
            r.saves.total.attempts > 0
                ? Math.round((r.saves.total.saves / r.saves.total.attempts) * 100)
                : '-',
    },
];

export type VenueTag = 'home' | 'away';

/** Same shape as win/loss, keyed by home/away. */
export const buildVenuePlayerSplits = (lines: TwPlayerMatchLine[]): ResultSplitRow[] => {
    const all = emptySplit('all', 'All');
    const home = emptySplit('all', 'Home');
    home.result = 'all';
    const away = emptySplit('all', 'Away');

    const apply = (row: ResultSplitRow, line: TwPlayerMatchLine): void => {
        row.matches += 1;
        row.goals += line.board.shots.total.goals;
        row.shots += line.board.shots.total.attempts;
        row.assists += line.board.assists;
        row.exclusionsDrawn += line.board.exclusionsDrawn;
        row.turnovers += line.board.turnovers;
        row.saves += line.board.saves.total.saves;
        row.sog += line.board.saves.total.attempts;
    };

    lines.forEach((line) => {
        apply(all, line);
        if (line.isHome) apply(home, line);
        else apply(away, line);
    });

    return [all, home, away].filter((row) => row.matches > 0);
};

export interface SampleHalfComparison {
    label: string;
    older: number;
    newer: number;
    delta: number;
    lowerIsBetter?: boolean;
}

/** Older half of sample vs newer half (lines newest-first). */
export const buildSampleHalfComparison = (
    lines: TwPlayerMatchLine[]
): SampleHalfComparison[] => {
    if (lines.length < 4) return [];
    const mid = Math.floor(lines.length / 2);
    const newer = lines.slice(0, mid);
    const older = lines.slice(mid);

    const avg = (xs: TwPlayerMatchLine[], pick: (l: TwPlayerMatchLine) => number): number =>
        xs.length === 0 ? 0 : xs.reduce((s, l) => s + pick(l), 0) / xs.length;

    const metric = (
        label: string,
        pick: (l: TwPlayerMatchLine) => number,
        lowerIsBetter?: boolean
    ): SampleHalfComparison => {
        const olderValue = avg(older, pick);
        const newerValue = avg(newer, pick);
        return {
            label,
            older: olderValue,
            newer: newerValue,
            delta: newerValue - olderValue,
            lowerIsBetter,
        };
    };

    return [
        metric('Goals', (l) => l.board.shots.total.goals),
        metric('Shots', (l) => l.board.shots.total.attempts),
        metric('Assists', (l) => l.board.assists),
        metric('Exclusions drawn', (l) => l.board.exclusionsDrawn),
        metric('Turnovers', (l) => l.board.turnovers, true),
        metric('Saves', (l) => l.board.saves.total.saves),
    ];
};

export interface AssistThresholdRecord {
    threshold: number;
    games: number;
    wins: number;
    draws: number;
    losses: number;
}

/** When player records ≥ threshold assists, team W-D-L. */
export const buildAssistThresholdRecord = (
    lines: TwPlayerMatchLine[],
    threshold = 2
): AssistThresholdRecord | undefined => {
    const hit = lines.filter((l) => l.board.assists >= threshold && l.result != null);
    if (hit.length === 0) return undefined;
    return {
        threshold,
        games: hit.length,
        wins: hit.filter((l) => l.result === 'W').length,
        draws: hit.filter((l) => l.result === 'D').length,
        losses: hit.filter((l) => l.result === 'L').length,
    };
};

export interface FoulTroubleSummary {
    matchesWithOne: number;
    matchesWithTwoPlus: number;
    /** Average countdown minute of first exclusion (lower = earlier). */
    avgFirstMinute?: number;
    avgSecondMinute?: number;
    sampleFirst: number;
    sampleSecond: number;
}

/**
 * Exclusion timing for one player across bundles (our exclusions committed).
 * Uses countdown minute on the exclusion event.
 */
export const buildFoulTroubleSummary = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number
): FoulTroubleSummary => {
    let matchesWithOne = 0;
    let matchesWithTwoPlus = 0;
    const firstMinutes: number[] = [];
    const secondMinutes: number[] = [];

    bundles.forEach((bundle) => {
        const mpIds = new Set<number>();
        (bundle.detail.players || []).forEach((mp) => {
            if (mp.playerId === playerId && mp.id != null && ourTeamIds.includes(mp.teamId)) {
                mpIds.add(mp.id);
            }
        });
        if (mpIds.size === 0) return;

        const committed: { minute: number; periodId: number }[] = [];
        bundle.events.forEach((event) => {
            if (!event.exclusion) return;
            if (!ourTeamIds.includes(event.exclusion.teamId)) return;
            if (
                event.exclusion.excludedPlayerId == null ||
                !mpIds.has(event.exclusion.excludedPlayerId)
            ) {
                return;
            }
            committed.push({
                minute: event.minute ?? 0,
                periodId: event.periodId,
            });
        });

        // Chronological: period then more time remaining first (same as play order).
        committed.sort((a, b) =>
            a.periodId !== b.periodId ? a.periodId - b.periodId : b.minute - a.minute
        );

        if (committed.length >= 1) {
            matchesWithOne += 1;
            firstMinutes.push(committed[0].minute);
        }
        if (committed.length >= 2) {
            matchesWithTwoPlus += 1;
            secondMinutes.push(committed[1].minute);
        }
    });

    const avg = (xs: number[]): number | undefined =>
        xs.length > 0 ? xs.reduce((s, v) => s + v, 0) / xs.length : undefined;

    return {
        matchesWithOne,
        matchesWithTwoPlus,
        avgFirstMinute: avg(firstMinutes),
        avgSecondMinute: avg(secondMinutes),
        sampleFirst: firstMinutes.length,
        sampleSecond: secondMinutes.length,
    };
};

export const exclusionNet = (drawn: number, committed: number): number => drawn - committed;

export const exclusionRatioText = (drawn: number, committed: number): string => {
    if (committed === 0) return drawn > 0 ? `${drawn}:0` : '-';
    return `${(drawn / committed).toFixed(1)}`;
};